import React, { createContext, useContext, useState, useEffect } from 'react';

interface DemoModeContextType {
  isDemoMode: boolean;
  enableDemoMode: () => void;
  disableDemoMode: () => void;
  toggleDemoMode: () => void;
  showDemoData: boolean;
  setShowDemoData: (show: boolean) => void;
}

const DemoModeContext = createContext<DemoModeContextType | undefined>(undefined);

export const useDemoMode = () => {
  const context = useContext(DemoModeContext);
  if (!context) {
    throw new Error('useDemoMode must be used within a DemoModeProvider');
  }
  return context;
};

interface DemoModeProviderProps {
  children: React.ReactNode;
}

export const DemoModeProvider: React.FC<DemoModeProviderProps> = ({ children }) => {
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [showDemoData, setShowDemoData] = useState(false);

  // Check for demo mode on mount
  useEffect(() => {
    const demoMode = localStorage.getItem('demoMode') === 'true';
    const showDemo = localStorage.getItem('showDemoData') === 'true';
    
    setIsDemoMode(demoMode);
    setShowDemoData(showDemo);
  }, []);

  const enableDemoMode = () => {
    setIsDemoMode(true);
    localStorage.setItem('demoMode', 'true');
  };

  const disableDemoMode = () => {
    setIsDemoMode(false);
    localStorage.removeItem('demoMode');
  };

  const toggleDemoMode = () => {
    if (isDemoMode) {
      disableDemoMode();
    } else {
      enableDemoMode();
    }
  };

  const handleSetShowDemoData = (show: boolean) => {
    setShowDemoData(show);
    if (show) {
      localStorage.setItem('showDemoData', 'true');
    } else {
      localStorage.removeItem('showDemoData');
    }
  };

  const value: DemoModeContextType = {
    isDemoMode,
    enableDemoMode,
    disableDemoMode,
    toggleDemoMode,
    showDemoData,
    setShowDemoData: handleSetShowDemoData
  };

  return (
    <DemoModeContext.Provider value={value}>
      {children}
    </DemoModeContext.Provider>
  );
}; 